
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/24152be5-deb4-441f-94a9-4bdf3ca3021a.png" 
              alt="LOA d.o.o. Logo" 
              className="h-12 w-auto object-contain"
            />
            <h1 className={`text-2xl font-bold transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>
              LOA d.o.o.
            </h1>
          </div>
          <div className="flex gap-8">
            <a href="#home" className={`text-sm font-medium transition-colors hover:text-[#00D4FF] ${scrolled ? 'text-gray-900' : 'text-white'}`}>Home</a>
            <a href="#about" className={`text-sm font-medium transition-colors hover:text-[#00D4FF] ${scrolled ? 'text-gray-900' : 'text-white'}`}>About</a>
            <a href="#products" className={`text-sm font-medium transition-colors hover:text-[#00D4FF] ${scrolled ? 'text-gray-900' : 'text-white'}`}>Products</a>
            <a href="#contact" className={`text-sm font-medium transition-colors hover:text-[#00D4FF] ${scrolled ? 'text-gray-900' : 'text-white'}`}>Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2D1B69] via-[#0066FF] to-[#00D4FF]">
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/20 to-black/40"></div>
        </div>

        {/* Floating Device Mockups */}
        <div className="absolute right-[10%] top-[20%] w-[300px] h-[600px] opacity-20">
          <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-[40px] shadow-2xl transform rotate-[15deg]"></div>
        </div>
        <div className="absolute right-[25%] top-[35%] w-[280px] h-[560px] opacity-15">
          <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-[40px] shadow-2xl transform -rotate-[8deg]"></div>
        </div>
        <div className="absolute right-[15%] bottom-[15%] w-[320px] h-[640px] opacity-10">
          <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-[40px] shadow-2xl transform rotate-[22deg]"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-20 w-full">
          <div className="max-w-[700px]">
            <h2 className="text-[120px] font-bold text-white leading-[1.1] mb-8">
              LOA d.o.o.
            </h2>
            <div className="space-y-4">
              <p className="text-5xl font-medium text-white/95 leading-[1.4]">Expert iOS Development</p>
              <p className="text-5xl font-medium text-white/95 leading-[1.4]">Elevating Mobile Experience</p>
              <p className="text-5xl font-medium text-white/95 leading-[1.4]">Security-First Solutions</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-[#F8F7F5]">
        <div className="max-w-[1200px] mx-auto px-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B6B6B] mb-10">WHO WE ARE</p>
          <h3 className="text-6xl font-serif text-[#1A1A1A] leading-[1.3] mb-16 max-w-[900px] mx-auto" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            A team of experts developing modern applications for the iOS platform
          </h3>
          <p className="text-lg font-light text-[#4A4A4A] leading-[1.8] max-w-[800px] mx-auto">
            Our products are designed to enhance user experience and improve the security of mobile devices
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="mb-20">
            <p className="text-sm uppercase tracking-[0.3em] text-[#8A8A8A] mb-6">OUR PRODUCTS</p>
            <h3 className="text-[96px] font-bold text-black leading-[1.1]">iOS Solutions</h3>
          </div>

          <div className="grid grid-cols-2 gap-[10%]">
            {/* VPN Card */}
            <div className="bg-gradient-to-br from-[#0A1628] to-[#1E3A5F] rounded-3xl p-12 shadow-[0_12px_40px_rgba(0,0,0,0.15)] transform hover:scale-[1.02] transition-transform duration-300">
              <div className="w-20 h-20 flex items-center justify-center mb-8">
                <i className="ri-shield-check-line text-[80px] text-[#00D4FF]"></i>
              </div>
              <h4 className="text-[42px] font-bold text-white mb-6">VPN</h4>
              <div className="space-y-3 text-white/80 text-base font-light leading-relaxed">
                <p>Secure your connection</p>
                <p>Private browsing experience</p>
                <p>Global server network</p>
              </div>
            </div>

            {/* Cleaner Card */}
            <div className="bg-gradient-to-br from-[#FFF5F7] to-[#F0E6FF] rounded-3xl p-12 shadow-[0_12px_40px_rgba(0,0,0,0.15)] mt-20 transform hover:scale-[1.02] transition-transform duration-300">
              <div className="w-20 h-20 flex items-center justify-center mb-8">
                <i className="ri-brush-line text-[80px] text-[#6B4CE6]"></i>
              </div>
              <h4 className="text-[42px] font-bold text-[#2D1B69] mb-6">Cleaner</h4>
              <div className="space-y-3 text-[#4A4A4A] text-base leading-relaxed">
                <p>Optimize device performance</p>
                <p>Remove junk files</p>
                <p>Extend battery life</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[#0F0F0F] py-24 relative">
        {/* Noise Texture Overlay */}
        <div className="absolute inset-0 opacity-[0.03] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')]"></div>
        
        {/* Top Divider */}
        <div className="absolute top-0 left-0 right-0 h-px bg-[#2A2A2A]"></div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/30"></div>

        <div className="relative max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-4 gap-10">
            {/* Column 1 - Brand */}
            <div>
              <h4 className="text-[32px] font-bold text-white mb-3">LOA d.o.o.</h4>
              <p className="text-sm text-[#A0A0A0]">iOS Development Experts</p>
            </div>

            {/* Column 2 - Location */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-line text-xl text-[#00D4FF]"></i>
                </div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/60">LOCATION</p>
              </div>
              <div className="space-y-3 text-[15px] font-light text-white/90 leading-relaxed">
                <p>Juzna Magistrala 17</p>
                <p>Sibenik, 22000</p>
                <p>Croatia</p>
              </div>
            </div>

            {/* Column 3 - Contact */}
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/60 mb-4">GET IN TOUCH</p>
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-xl text-[#00D4FF]"></i>
                  </div>
                  <a href="mailto:help@loa-doo.com" className="text-[15px] text-white hover:text-[#00D4FF] transition-colors whitespace-nowrap">
                    help@loa-doo.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-xl text-[#00D4FF]"></i>
                  </div>
                  <a href="tel:+15074797388" className="text-[15px] text-white hover:text-[#00D4FF] transition-colors whitespace-nowrap">
                    +1 5074797388
                  </a>
                </div>
              </div>
            </div>

            {/* Column 4 - Copyright */}
            <div className="text-right">
              <p className="text-[13px] text-[#6B6B6B] mb-2">© 2025 LOA d.o.o.</p>
              <p className="text-xs text-[#4A4A4A]">All rights reserved</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
